import { GetStaticPaths, GetStaticProps } from 'next'
import { JobPostingJsonLd, NextSeo } from 'next-seo'

import { strapi } from 'api'
import { REVALIDATION_ERROR_TIMEOUT, REVALIDATION_TIMEOUT } from 'const/config'
import { BackendError } from 'api/strapi/BackendError'
import { Opportunity as OpportunityModel } from 'api/strapi/opportunities/types'
import { LinkButton } from 'components/links'
import { Section } from 'components/section'
import { Heading } from 'components/heading'
import { Button } from 'components/button'
import { Markdown } from 'components/markdown'
import { routes } from 'utils/routes'
import { RouterDialog, useControlRouterDialog } from 'components/dialog'
import { DIALOG_NAME } from 'const/dialogs'
import { JoinUsForm } from 'components/join-us-form/join-us-form'

import classes from './opportunity.module.css'

export default function Opportunity({ details, subtitle, title, id, createdAt }: OpportunityModel) {
  const { open } = useControlRouterDialog()

  return (
    <>
      <NextSeo title={title} description={`We’re looking for a ${title}`} />
      <JobPostingJsonLd
        datePosted={createdAt}
        description={`We’re looking for a ${title}`}
        hiringOrganization={{
          name: 'Rezitrade',
          logo: '/single-letter-logo.svg',
          sameAs: routes.opportunity(id),
        }}
        title={title}
      />
      <div className={classes.mainContainer}>
        <div className={classes.goBackContainer}>
          <Section theme="withoutGap" htmlElement="div">
            <LinkButton theme="goBack" href={routes.joinUs}>
              back to all opportunities
            </LinkButton>
          </Section>
        </div>

        <Section className={classes.content}>
          <Heading theme="h2" component="h2">
            {title}
          </Heading>
          <span className={classes.subtitle}>{subtitle}</span>
          <Button
            theme="secondary"
            className={classes.applyNow}
            onClick={() => open(DIALOG_NAME.joinUsSendInvitation)}
          >
            Apply Now
          </Button>
          <Markdown className={classes.details}>{details}</Markdown>
        </Section>
        <RouterDialog modalName={DIALOG_NAME.joinUsSendInvitation} title="Information about you">
          <JoinUsForm vacancySubtitle={subtitle} vacancyTitle={title} vacancyId={id} />
        </RouterDialog>
      </div>
    </>
  )
}

export const getStaticProps: GetStaticProps<OpportunityModel> = async (context) => {
  const { id } = context.params ?? {}
  if (!id)
    return {
      revalidate: REVALIDATION_ERROR_TIMEOUT,
      notFound: true,
    }

  try {
    const opportunity = await strapi.opportunities.findOne(String(id))

    return {
      revalidate: REVALIDATION_TIMEOUT,
      props: opportunity,
    }
  } catch (error) {
    if ((error as BackendError).notFound) {
      return {
        notFound: true,
        revalidate: REVALIDATION_ERROR_TIMEOUT,
      }
    }
    throw error
  }
}

export const getStaticPaths: GetStaticPaths = async () => {
  const ids = await strapi.opportunities.listIds()
  return {
    fallback: 'blocking',
    paths: ids.map((id) => ({
      params: {
        id: String(id),
      },
    })),
  }
}
